import type {State} from './types';
import {Status} from './constants';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const selectTutorialStates = <S extends any>(state: S): State =>
  // @ts-expect-error - TS2339 - Property 'FTUStore' does not exist on type 'S'.
  state.FTUStore;

export function isAnyTutorialActive(state: State): boolean {
  for (const key in state.tutorials) {
    if (isTutorialActive(key, state)) {
      return true;
    }
  }
  return false;
}

export function isTutorialActive(
  tutorialId: keyof State['tutorials'],
  state: State
): boolean {
  if (state.tutorials[tutorialId]) {
    return state.tutorials[tutorialId].status === Status.Active;
  }

  return false;
}

export function selectActiveTutorial(state: State): null | string {
  const activeTutorial = state.pending.tutorial;
  if (activeTutorial && isTutorialActive(activeTutorial, state)) {
    return activeTutorial;
  }

  return null;
}

export function selectActiveTutorialStep(state: State): null | string {
  const activeTutorial = selectActiveTutorial(state);
  if (activeTutorial) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    return state.tutorials[activeTutorial].currentStepId;
  }

  return null;
}

export function isTutorialStepActive(
  tutorialId: keyof State['tutorials'],
  state: State,
  predicate: (stepId: string | null) => boolean
): boolean {
  if (
    state.tutorials[tutorialId] &&
    state.tutorials[tutorialId].status === Status.Active
  ) {
    return predicate(state.tutorials[tutorialId].currentStepId);
  }

  return false;
}

export function isTutorialCompleted(
  tutorialId: keyof State['tutorials'],
  state: State
): boolean {
  if (state.tutorials[tutorialId]) {
    return state.tutorials[tutorialId].status === Status.Completed;
  }

  return false;
}

export function canTutorialResume(
  tutorialId: keyof State['tutorials'],
  state: State
): boolean {
  return Boolean(state.tutorials[tutorialId]?.canResume);
}

export function shouldTutorialHideSections(state: State): boolean {
  return evaluateOption(state, 'hideCollapsedSections');
}

export function shouldTutorialDisableShortcuts(state: State): boolean {
  return evaluateOption(state, 'disableShortcuts');
}

export function shouldTutorialHideStylePanelHighlights(state: State): boolean {
  return evaluateOption(state, 'hideStylePanelHighlights');
}

export function shouldTutorialHideBreadcrumbs(state: State): boolean {
  return evaluateOption(state, 'hideBreadcrumbs');
}

export function shouldTutorialHideMiniSettings(state: State): boolean {
  return evaluateOption(state, 'hideMiniSettings');
}

export function shouldTutorialHideBreakpointButtonTooltips(
  state: State
): boolean {
  return evaluateOption(state, 'hideBreakpointButtonTooltips');
}

export function shouldTutorialHideAddBarTooltips(state: State): boolean {
  return evaluateOption(state, 'hideAddBarTooltips');
}

export function shouldTutorialForceNavigatorRightWhileDragging(
  state: State
): boolean {
  return evaluateOption(state, 'forceNavigatorRightWhileDragging');
}

export function shouldTutorialHideColorPicker(state: State): boolean {
  return evaluateOption(state, 'hideColorPicker');
}

export function shouldTutorialHideTopBar(state: State): boolean {
  return evaluateOption(state, 'hideTopBar');
}

function evaluateOption(state: State, optionName: string) {
  const tutorialId = selectActiveTutorial(state);
  const activeTutorial = tutorialId && state.tutorials[tutorialId];
  if (!activeTutorial) return false;

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ hideCollapsedSections?: TutorialConfigOption | undefined; disableShortcuts?: TutorialConfigOption | undefined; hideStylePanelHighlights?: TutorialConfigOption | undefined; ... 6 more ...; hideTopBar?: TutorialConfigOption | undefined; }'.
  const optionValue = activeTutorial.opts?.[optionName];
  if (typeof optionValue === 'boolean') return optionValue;
  if (typeof optionValue === 'function')
    return optionValue(activeTutorial.currentStepId);
  return false;
}
